import { Book1, type Icon, Logout, Messages1, ProfileCircle } from 'iconsax-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';
import { useOnClickOutside } from 'usehooks-ts';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import AppDownloadPrompt from '@/components/layouts/page-layout/app-download-prompt';
import AppHeaderMenu from '@/components/layouts/page-layout/app-header-menu';
import useFriendRequestCount from '@/hooks/contacts/use-friend-request-count';
import { useSessionUnreadCount } from '@/hooks/sessions/use-conversation-sessions';
import useUserPlatform from '@/hooks/use-user-platform';
import useSignOut from '@/hooks/users/use-sign-out';
import useUserInfo from '@/hooks/users/use-user-info';
import ROUTES from '@/routes';
import { makePrompt } from '@/stores/prompter';
import cn from '@/utils/merge-class-names';

interface NavItemType {
  key: string;
  label: string;
  icon: Icon;
  count?: number;
  className?: string;
  show?: boolean | 'label' | 'icon';
}

export default function BottomBar() {
  const { t } = useTranslation();
  const detailRef = useRef<HTMLDetailsElement>(null);
  const { isDesktop, mode, isEmbedded } = useUserPlatform();
  const { data: userInfo } = useUserInfo();
  const { friendRequestCount } = useFriendRequestCount();
  const { mutateAsync: signOut } = useSignOut();
  const { unreadCount } = useSessionUnreadCount(true);

  const handleSignOut = () =>
    makePrompt({
      key: 'sign-out',
      title: t('general-settings.log-out-confirm'),
      promise: () => signOut(),
    });

  const navItems: NavItemType[] = [
    {
      key: ROUTES.CONTACT.$path({ relative: true }),
      label: 'nav.contact',
      icon: Book1,
      show: mode === 'embedded' ? 'label' : true,
      count: friendRequestCount,
    },
    {
      key: ROUTES.MESSAGES.$path({ relative: true }),
      label: 'nav.messages',
      icon: Messages1,
      show: mode === 'embedded' ? 'label' : true,
      count: unreadCount,
    },
    {
      key: ROUTES.MY.$path({ relative: true }),
      label: 'nav.my',
      icon: ProfileCircle,
      show: !isDesktop && mode !== 'embedded',
    },
  ];

  useOnClickOutside(detailRef, () => detailRef.current?.removeAttribute('open'));

  return (
    <div className='fixed desktop:absolute inset-x-0 desktop:top-0 desktop:right-auto desktop:bg-primary desktop:flex-col bottom-0 z-50 flex items-center desktop:justify-start justify-evenly bg-neutral-0 p-2 desktop:gap-8 desktop:px-5 desktop:py-6 shadow shadow-black/5 pb-safe-or-2 mode-embedded:top-0 mode-embedded:pt-safe-offset-0 mode-embedded:bottom-auto mode-embedded:justify-end mode-embedded:flex-row-reverse mode-embedded:shadow mode-embedded:bg-primary mode-embedded:pb-0'>
      {isDesktop && userInfo && (
        <details
          className='dropdown dropdown-right dropdown-end dropdown-bottom'
          ref={detailRef}>
          <summary className='btn btn-xs btn-ghost p-0'>
            <AvatarWithStatus
              imageUrl={undefined}
              label={userInfo.NickName}
              className='size-12'
              size={[48, 48]}
            />
          </summary>

          <ul className='menu dropdown-content bg-neutral-0 translate-x-2 p-2 shadow rounded w-40'>
            <li>
              <button
                type='button'
                className='btn btn-ghost text-error'
                onClick={handleSignOut}>
                <Logout
                  variant='Outline'
                  size={20}
                  className='size-8 rounded-lg p-1.5'
                />

                {t('general-settings.log-out')}
              </button>
            </li>
          </ul>
        </details>
      )}

      {navItems
        .filter(({ show = true }) => show)
        .map(({ key, label, count, show = true, icon: Icon, className }) => (
          <NavLink
            state={isEmbedded ? { from: 'comm' } : null}
            key={key}
            to={`/${key}`}
            className={cn(
              'flex basis-15vw desktop:basis-14 desktop:size-14 desktop:aspect-square flex-col flex-center mode-embedded:grow-0',
              className,
            )}>
            {({ isActive }) => (
              <div
                className={cn(
                  'flex indicator h-full mode-embedded:py-4 mode-embedded:after:content-dummy mode-embedded:after:absolute mode-embedded:after:inset-x-2 mode-embedded:after:bottom-0.5 mode-embedded:after:h-1 mode-embedded:after:rounded-full transition-colors flex-col flex-center desktop:h-auto',
                  {
                    'mode-embedded:after:bg-primary-content': isActive,
                  },
                )}>
                <Icon
                  className={cn(
                    'size-8 p-0.5 mode-embedded:text-primary-content mode-embedded:size-6 mode-embedded:p-0',
                    { hidden: show === 'label' || !show },
                    isActive
                      ? 'text-primary mode-embedded:text-primary-content desktop:text-neutral-0'
                      : 'theme-ryuryu:desktop:text-blue-200 theme-wangyou:desktop:text-teal-200 group-data-sender:theme-threetwoone:bg-purple-200 group-data-sender:theme-eightk:bg-violet-200 group-data-sender:theme-bonglan:bg-red-200 text-body',
                  )}
                  variant={isActive ? 'Bold' : 'Linear'}
                />

                {count ? (
                  count === Number.POSITIVE_INFINITY ? (
                    <span className='badge badge-accent bg-red-500 size-1.5 indicator-item ring-1 ring-neutral-0 p-0 border-0 -top-px right-[-5px]' />
                  ) : (
                    <span
                      className={cn(
                        'badge badge-accent bg-red-500 text-neutral-0 size-4 indicator-item text-[0.625rem] ring-2 border-0 ring-neutral-0 p-0 -top-px right-[-9px]',
                        {
                          'w-[21px]': count !== Number.POSITIVE_INFINITY && count > 99,
                        },
                      )}>
                      {count > 99 ? '99+' : count}
                    </span>
                  )
                ) : null}

                <span
                  className={cn(
                    'text-xs mode-embedded:text-[1rem] mode-embedded:text-primary-content',
                    { hidden: show === 'icon' || !show },
                    isActive
                      ? 'text-primary mode-embedded:font-bold mode-embedded:text-primary-content desktop:text-neutral-0'
                      : 'text-body theme-ryuryu:desktop:text-blue-200 theme-wangyou:desktop:text-teal-200 group-data-sender:theme-threetwoone:bg-purple-200 group-data-sender:theme-eightk:bg-violet-200 group-data-sender:theme-bonglan:bg-red-200',
                  )}>
                  {t(label)}
                </span>
              </div>
            )}
          </NavLink>
        ))}

      <AppHeaderMenu className='hidden mode-embedded:block mode-embedded:-order-2' />

      <div className='flex-1 desktop:block hidden mode-embedded:block mode-embedded:-order-2' />

      <AppDownloadPrompt />
    </div>
  );
}
